import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import io from "socket.io-client";
import { BASE_URL } from "../../redux/ApiEndPoints";

export default function Notification() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const socketRef = useRef(null);
  const bellRef = useRef();
  const auth = useSelector((state) => state.auth);

  // Fetch notifications from API on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(BASE_URL + "notifications", {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.notifications)) {
          setNotifications(
            data.notifications.map((n) => ({
              ...n,
              timestamp:
                n.created_at || n.timestamp || new Date().toISOString(),
            }))
          );
          setUnreadCount(data.notifications.filter((n) => !n.is_read).length);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    if (auth?.token) fetchNotifications();
  }, [auth?.token]);

  const studentId = auth?.user_id;

  // Socket.IO for student notifications
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      if (studentId) {
        socket.emit("joinStudentRoom", { studentId });
      }
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    socket.on("studentNotification", (notification) => {
      setNotifications((prev) => [
        {
          ...notification,
          timestamp: notification.timestamp || new Date().toISOString(),
          is_read: false,
        },
        ...prev,
      ]);
      setUnreadCount((prev) => prev + 1);
    });

    socket.on("disconnect", () => {});

    return () => {
      socket.disconnect();
    };
  }, [studentId]);

  // Mark notifications as read when dropdown is opened
  useEffect(() => {
    if (dropdownOpen && notifications.length > 0) {
      const unreadIds = notifications
        .filter((n) => !n.is_read)
        .map((n) => n.id)
        .filter(Boolean);
      if (unreadIds.length > 0) {
        fetch(BASE_URL + "notifications/mark-read", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ ids: unreadIds }),
        }).then(() => {
          setNotifications((prev) =>
            prev.map((n) =>
              unreadIds.includes(n.id) ? { ...n, is_read: true } : n
            )
          );
        });
      }
      setUnreadCount(0);
    }
  }, [dropdownOpen, notifications, auth?.token]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Get notification icon based on event type
  const getNotificationIcon = (event) => {
    switch (event) {
      // Learning Progress & Engagement
      case "progress-reminder":
        return "📈";
      case "inactivity-reminder":
        return "⏰";
      case "weekly-report":
        return "📊";
      case "course-enrolled":
        return "🟢";
      case "course-unenrolled":
        return "🔴";
      
      // Notebook LM Features
      case "notebook-chat":
        return "💬";
      case "quiz-completed":
        return "🧠";
      case "flashcard-created":
        return "🎴";
      case "note-created":
        return "📝";
      case "mindmap-generated":
        return "🗺️";
      case "audio-summary-generated":
        return "🎵";
      case "learning-milestone":
        return "🏆";
      
      // AI Bots & Assessments
      case "assessme-completed":
        return "📋";
      case "growth-coordinator-message":
        return "🤖";
      case "certifyme-assessment":
        return "🎯";
      case "assessment-score-improved":
        return "📈";
      
      // Certificates
      case "certificate-requested":
        return "📋";
      case "certificate-approved":
        return "✅";
      case "certificate-rejected":
        return "❌";
      case "certificate-ready":
        return "📜";
      case "lutinx-login-required":
        return "🔐";
      
      // Course Management
      case "course-published":
        return "🚀";
      case "course-unpublished":
        return "🛑";
      case "course-updated":
        return "✏️";
      
      // Account Management
      case "account-deactivated":
        return "🔒";
      case "account-activated":
        return "🔓";
      
      // Admin Actions
      case "profile-updated":
        return "👤";
      case "education-updated":
        return "🎓";
      case "experience-updated":
        return "💼";
      
      // System Notifications
      case "system-maintenance":
        return "🔧";
      case "feature-update":
        return "🆕";
      
      default:
        return "🔔";
    }
  };

  // Get notification category for styling
  const getNotificationCategory = (event) => {
    // Success notifications (positive actions)
    if (
      [
        "course-enrolled",
        "account-activated",
        "certificate-approved",
        "certificate-ready",
        "quiz-completed",
        "learning-milestone",
        "assessment-score-improved",
        "course-published",
      ].includes(event)
    )
      return "success";
    
    // Danger notifications (negative actions)
    if (
      [
        "course-unenrolled",
        "account-deactivated",
        "certificate-rejected",
        "course-unpublished",
      ].includes(event)
    )
      return "danger";
    
    // Warning notifications (reminders/attention needed)
    if (
      [
        "progress-reminder",
        "inactivity-reminder",
        "lutinx-login-required",
        "system-maintenance",
      ].includes(event)
    )
      return "warning";
    
    // Info notifications (general updates)
    if (
      [
        "weekly-report",
        "notebook-chat",
        "flashcard-created",
        "note-created",
        "mindmap-generated",
        "audio-summary-generated",
        "assessme-completed",
        "growth-coordinator-message",
        "certifyme-assessment",
        "certificate-requested",
        "course-updated",
        "feature-update",
        "profile-updated",
        "education-updated",
        "experience-updated",
      ].includes(event)
    )
      return "info";
    
    return "default";
  };

  function formatStudentNotification({ event, data }) {
    let parsedData;
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
      console.error("Failed to parse notification data:", error);
      return "Invalid notification data.";
    }

    switch (event) {
      // Learning Progress & Engagement
      case "progress-reminder":
        return `Keep going! You are ${parsedData.progress}% done with ${parsedData.courseName}.`;
      case "inactivity-reminder":
        return `You haven't visited ${parsedData.courseName} in ${parsedData.daysInactive} days.`;
      case "weekly-report":
        return `You spent ${parsedData.hoursSpent} hours on ${parsedData.courseName} this week.`;
      case "course-enrolled":
        return `You have enrolled in ${parsedData.courseName}.`;
      case "course-unenrolled":
        return `You have been unenrolled from ${parsedData.courseName}.`;
      
      // Notebook LM Features
      case "notebook-chat":
        return `New message in your ${parsedData.courseName} notebook chat.`;
      case "quiz-completed":
        return `Quiz generated in ${parsedData.courseName}! Questions: ${parsedData.questionCount}`;
      case "flashcard-created":
        return `New flashcard set created for ${parsedData.courseName}.`;
      case "note-created":
        return `New note saved in ${parsedData.courseName} notebook.`;
      case "mindmap-generated":
        return `Mind map generated for ${parsedData.courseName} topic.`;
      case "audio-summary-generated":
        return `Audio summary ready for ${parsedData.courseName} content.`;
      case "learning-milestone":
        return `Congratulations! You've reached a learning milestone in ${parsedData.courseName}.`;
      
      // AI Bots & Assessments
      case "assessme-completed":
        return `AssessMe assessment completed! Your score: ${parsedData.score}%`;
      case "growth-coordinator-message":
        return `Growth Coordinator has a new message for you.`;
      case "certifyme-assessment":
        return `CertifyMe assessment completed! Score: ${parsedData.score}%`;
      case "assessment-score-improved":
        return `Great improvement! Your assessment score increased by ${parsedData.improvement}%`;
      
      // Certificates
      case "certificate-requested":
        return `Certificate request submitted for ${parsedData.courseName}.`;
      case "certificate-approved":
        return `Congratulations! Your certificate for ${parsedData.courseName} has been approved.`;
      case "certificate-rejected":
        return `Certificate request for ${parsedData.courseName} was rejected. ${parsedData.reason || ""}`;
      case "certificate-ready":
        return `Your certificate for ${parsedData.courseName} is ready for download!`;
      case "lutinx-login-required":
        return `Please login via Lutinx to complete your certificate process.`;
      
      // Course Management
      case "course-published":
        return `Course ${parsedData.courseName} by ${parsedData.instructorName} has been published!`;
      case "course-unpublished":
        return `Course ${parsedData.courseName} by ${parsedData.instructorName} has been unpublished.`;
      case "course-updated":
        return `Course ${parsedData.courseName} has been updated with new content.`;
      
      // Account Management
      case "account-deactivated":
        return `Your account has been deactivated. ${parsedData.reason || ""}`;
      case "account-activated":
        return `Your account has been reactivated. Welcome back!`;
      
      // Admin Actions
      case "profile-updated":
        const changes = parsedData.changes || {};
        const changeList = Object.values(changes).filter(Boolean);
        return `Your profile has been updated by administrator. Changes: ${changeList.join(', ')}`;
      case "education-updated":
        const eduChanges = parsedData.changes || {};
        const eduChangeList = Object.values(eduChanges).filter(Boolean);
        return `Your education information has been updated by administrator. Changes: ${eduChangeList.join(', ')}`;
      case "experience-updated":
        const expChanges = parsedData.changes || {};
        const expChangeList = Object.values(expChanges).filter(Boolean);
        return `Your experience information has been updated by administrator. Changes: ${expChangeList.join(', ')}`;
      
      // System Notifications
      case "system-maintenance":
        return `System maintenance scheduled for ${parsedData.date || "soon"}.`;
      case "feature-update":
        return `New feature available: ${parsedData.featureName || "Check it out!"}`;
      
      default:
        return "You have a new notification.";
    }
  }

  // Clear all notifications
  const handleClearAll = async () => {
    await fetch(BASE_URL + "notifications/clear", {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    setNotifications([]);
    setUnreadCount(0);
  };
  return (
    <div ref={bellRef} style={{ position: "relative" }}>
      <div
        className="notification-bell-container"
        onClick={() => {
          setDropdownOpen((open) => !open);
        }}
        style={{
          position: "relative",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: "#F05203",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
          transition: "all 0.3s ease",
          transform: dropdownOpen ? "scale(1.05)" : "scale(1)",
        }}
      >
        <span
          className="icon-bell"
          style={{
            fontSize: "20px",
            color: "#fff",
          }}
        />
        {unreadCount > 0 && (
          <div
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              background: "#F05203",
              color: "#fff",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              border: "2px solid #fff",
              animation: "pulse 2s infinite",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </div>
        )}
      </div>
      {dropdownOpen && (
        <div
          className="notification-dropdown"
          style={{
            position: "absolute",
            right: "0",
            top: "56px",
            maxWidth: "400px",
            width: "calc(100vw - 64px)",
            maxHeight: "500px",
            background: "#fff",
            borderRadius: "16px",
            boxShadow:
              "0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(0, 0, 0, 0.08)",
            zIndex: 1000,
            overflow: "hidden",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              padding: "20px 24px",
              background: "#F05203",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#fff",
                }}
              >
                Notifications
              </h4>
              <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
                {notifications.length}{" "}
                {notifications.length <= 1 ? "notification" : "notifications"}
              </p>
            </div>
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "8px",
                  color: "#fff",
                  padding: "6px 12px",
                  fontSize: "12px",
                  cursor: "pointer",
                  transition: "background 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                }
              >
                Clear All
              </button>
            )}
          </div>
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              padding: "0",
            }}
          >
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "40px 24px",
                  textAlign: "center",
                  color: "#9ca3af",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>🎓</div>
                <p style={{ margin: 0, fontSize: "16px", fontWeight: "500" }}>
                  No notifications yet
                </p>
                <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                  We'll keep you updated on your learning progress and course
                  activities
                </p>
              </div>
            ) : (
              notifications.map((notif, idx) => {
                const category = getNotificationCategory(notif.event);
                const isUnread = idx < unreadCount;
                return (
                  <div
                    key={idx}
                    style={{
                      padding: "16px 24px",
                      borderBottom:
                        idx === notifications.length - 1
                          ? "none"
                          : "1px solid #f3f4f6",
                      background: isUnread ? "#f8faff" : "#fff",
                      position: "relative",
                      transition: "background 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isUnread) e.target.style.background = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      if (!isUnread) e.target.style.background = "#fff";
                    }}
                  >
                    {isUnread && (
                      <div
                        style={{
                          position: "absolute",
                          left: "0",
                          top: "0",
                          bottom: "0",
                          width: "4px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        }}
                      />
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background:
                            category === "success"
                              ? "#ecfdf5"
                              : category === "danger"
                              ? "#fef2f2"
                              : category === "warning"
                              ? "#fffbeb"
                              : category === "info"
                              ? "#eff6ff"
                              : "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "18px",
                          flexShrink: 0,
                        }}
                      >
                        {getNotificationIcon(notif.event)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "1.4",
                            color: "#374151",
                            marginBottom: "4px",
                            fontWeight: isUnread ? "500" : "400",
                          }}
                        >
                          {formatStudentNotification(notif)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>⏰</span>
                          {notif.timestamp
                            ? new Date(notif.timestamp).toLocaleString()
                            : "Just now"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-bell-container:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
        }

        .notification-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .notification-dropdown::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .notification-dropdown::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .notification-dropdown::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
}
